import { Box, Divider, Link, Stack, Typography } from "@mui/material";
import { FormRecipientType, type FormSubmissionPropertyType } from "src/constants";
import { useSession } from "src/hooks";
import type { FormSubmissionDetailDto } from "src/http";
import { getFormSubmissionName } from "src/utilities";
import { FormSubmissionCreatedProperty } from "./FormSubmissionCreatedProperty";
import { FormSubmissionCreatorProperty } from "./FormSubmissionCreatorProperty";
import { FormSubmissionPeriodProperty } from "./FormSubmissionPeriodProperty";
import { FormSubmissionRecipientProperty } from "./FormSubmissionRecipientProperty";
import { FormSubmissionReferencedStrategicItemsProperty } from "./FormSubmissionReferencedStrategicItemsProperty";
import { FormSubmissionReferencedTasksProperty } from "./FormSubmissionReferencedTasksProperty";
import { FormSubmissionReviewedInOneOnOneProperty } from "./FormSubmissionReviewedInOneOnOneProperty";
import { FormSubmissionReviewedProperty } from "./FormSubmissionReviewedProperty";
import { FormSubmissionSubmittedProperty } from "./FormSubmissionSubmittedProperty";
import { FormSubmissionVideoUrlProperty } from "./FormSubmissionVideoUrlProperty";
import { FormSubmissionVisibilityProperty } from "./FormSubmissionVisibilityProperty";

interface Props {
  formSubmission: FormSubmissionDetailDto;
  propertyVisibility?: {
    [key in FormSubmissionPropertyType]?: boolean;
  };
  hideBackButton?: boolean;
  onBackClicked?: () => void;
}

const FormSubmissionProperties = (props: Props) => {
  const { userId: sessionUserId } = useSession();

  return (
    <Stack spacing={0.5} divider={<Divider variant="dashed" />}>
      <Box>
        {!props.hideBackButton && (
          <Link
            href="#"
            variant="label"
            onClick={(e) => {
              e.preventDefault();
              props.onBackClicked?.();
            }}
          >
            All Feedback
          </Link>
        )}
        <Typography variant="h4">
          {getFormSubmissionName(
            props.formSubmission.formName,
            props.formSubmission.dateRangeStartUtc,
            props.formSubmission.dateRangeEndUtc,
          )}
        </Typography>
      </Box>

      {props.formSubmission.dateRangeStartUtc != null && props.formSubmission.dateRangeEndUtc != null && (
        <FormSubmissionPeriodProperty
          dateRangeStartUtc={props.formSubmission.dateRangeStartUtc}
          dateRangeEndUtc={props.formSubmission.dateRangeEndUtc}
        />
      )}

      {props.formSubmission.recipientType !== FormRecipientType.Self && (
        <FormSubmissionRecipientProperty user={props.formSubmission.recipientUser} />
      )}

      <FormSubmissionCreatorProperty formSubmission={props.formSubmission} />

      {props.formSubmission.videoUrl && <FormSubmissionVideoUrlProperty videoUrl={props.formSubmission.videoUrl} />}

      <FormSubmissionVisibilityProperty formSubmission={props.formSubmission} />

      <FormSubmissionCreatedProperty createdDateTimeUtc={props.formSubmission.createdDateTimeUtc} />

      {props.formSubmission.submittedDateTimeUtc && (
        <FormSubmissionSubmittedProperty submittedDateTimeUtc={props.formSubmission.submittedDateTimeUtc} />
      )}

      {props.formSubmission.requiresReviewByRecipient && (
        <FormSubmissionReviewedProperty formSubmission={props.formSubmission} />
      )}

      {props.propertyVisibility?.reviewedInOneOnOne &&
        props.formSubmission.requiresReviewInOneOnOne &&
        props.formSubmission.recipientUserPeopleLead?.userId === sessionUserId && (
          <FormSubmissionReviewedInOneOnOneProperty formSubmission={props.formSubmission} />
        )}

      {props.formSubmission.referencedActionItems.length > 0 && (
        <FormSubmissionReferencedStrategicItemsProperty formSubmission={props.formSubmission} />
      )}

      {props.formSubmission.referencedTaskItems.length > 0 && (
        <FormSubmissionReferencedTasksProperty formSubmission={props.formSubmission} />
      )}
    </Stack>
  );
};

export { FormSubmissionProperties };
